import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDMSContext } from '../context/DMSContext';
import MyButton from '../UI/button/MyButton';
import Card from '../UI/Card/Card';
import InputWrapper from '../components/InputWrapper';
import debtorService from '../services/DebtorService';
import MySelect from '../UI/select/MySelect';
import { contractStatuses, currencies, debtorStatuses, paysFrequency } from '../assets/data/option';
import contractService from '../services/ContractService';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import Overlay from '../components/Overlay';
import GenericTable from '../UI/table/GenericTable';
import { format } from 'date-fns';
import CustomDatePicker from '../components/CustomDatePicker';

export default function DebtorDetailsPage() {
  const { id } = useParams();
  const { getDebtorById, updateDebtor, refetchDebtors, refetchContracts, getContractsByDebtor } = useDMSContext();
  const [debtor, setDebtor] = useState(getDebtorById(id));
  const navigate = useNavigate();
  const [contracts, setContracts] = useState(getContractsByDebtor(id) || []);
  const [contract, setContract] = useState({
    name: "",
    value: "",
    initialDue: "",
    status: "ACTIVE",            
    currency: 1,          
    penaltyPercentPerDay: "",
    payFrequency: "MONTHLY",     
    startDate: null,
    endDate: null,
    obligationDate: null,
    debtorId: id,         
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [overlay, setOverlay] = useState(false);

  const handleOverlay = () => {
    setOverlay(!overlay);
  }

  const handleEditContract = (contractData) => {
    setContract(contractData);
    setIsEditingContract(true);
    setOverlay(true);
  };

  const deleteContract = async (contractId) => {
    try {
      const response = await contractService.deleteContract(contractId);
      refetchContracts();
      toast.success('Contractul a fost șters cu succes');
    } catch (error) {
      toast.error('Eroare la ștergerea contractului');
    }
  };

  const validate = () => {
    if (contract.debtorId == 0 || contract.debtorId == null){
      toast.error('Selectați un debitor');
      return false;
    }
  
    if (contract.name == '' || contract.name == null){
      toast.error('Introduceți denumirea contractului');
      return false;
    }
  
    if (contract.startDate == null){
      toast.error('Selectați data de început');
      return false;
    }
  
    if (contract.endDate == null){
      toast.error('Selectați data de sfârșit');
      return false;
    }
  
    if (contract.obligationDate == null){
      toast.error('Selectați data obligației');
      return false;
    }
  
    if (contract.value == null || contract.value == ""){
      toast.error('Introduceți valoarea contractului');
      return false;
    }
  
    if (contract.initialDue == null || contract.initialDue == ""){
      toast.error('Introduceți suma scadentă inițială');
      return false;
    }
  
    return true;
  };
  

  const submitContract = async (e) => {
    e.preventDefault();
  
    if (!validate()) {
      return;
    }
  
    if (isEditingContract) {
      try {
        const response = await contractService.updateContract(contract.id, contract);
        refetchContracts();
        refetchDebtors();
        toast.success('Contractul a fost actualizat cu succes');
        setContracts(getContractsByDebtor(id));
      } catch (error) {
        toast.error('Eroare la actualizarea contractului');
      }
    } else {
      try {
        const response = await contractService.addContract(contract);
        refetchContracts();
        refetchDebtors();
        toast.success('Contractul a fost adăugat cu succes');
        setContracts(getContractsByDebtor(id));
      } catch (error) {
        toast.error('Eroare la adăugarea contractului');
      }
    }
  
    setContract({
      name: "",
      value: "",
      initialDue: "",
      status: "ACTIVE",
      currency: 1,
      penaltyPercentPerDay: "",
      payFrequency: "MONTHLY",
      startDate: null,
      endDate: null,
      obligationDate: null,
      debtorId: id
    });
    setIsEditing(false);
    setOverlay(false);
  };

  const handleChange = (field, value) => {
    setDebtor(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await debtorService.updateDebtor(debtor.id, debtor);
      toast.success('Modificările au fost salvate cu succes');
      refetchDebtors();
    } catch (error) {
      toast.error('Eroare la salvarea modificărilor');
    }finally{
      setIsEditing(false);
    }
  };

  const contractColumns = [
    { Header: 'NUMĂR CONTRACT', accessor: 'name' },
    { Header: 'DEBITOR', accessor: 'debtorName' },
    { Header: 'DATA ÎNCHEIERII', accessor: 'startDate',
    Cell: ({ value }) => {
      try {
        const date = new Date(value);
        return isNaN(date) ? '' : format(date, 'dd.MM.yyyy');
      } catch {
        return '';
      }
    } },
    { Header: 'DATA FINALIZĂRII', accessor: 'endDate',
    Cell: ({ value }) => {
      try {
        const date = new Date(value);
        return isNaN(date) ? '' : format(date, 'dd.MM.yyyy');
      } catch {
        return '';
      }
    } },
    {
      Header: 'VALOARE CONTRACT',
      accessor: 'value',
      Cell: ({ row }) => {
        const { value, currency } = row.original;
        return <span>{parseFloat(value).toFixed(2)} {currency}</span>;
      }
    },
    {
      Header: 'RESTANȚĂ CURENTĂ',
      accessor: 'debt',
      Cell: ({ row }) => {
        const { debt, currency } = row.original;
        return <span className={debt > 0 ? 'text-danger' : ''}>
          {parseFloat(debt).toFixed(2)} {currency}
        </span>
      }
    },
    {
      Header: 'PENALITĂȚI',
      accessor: 'penalty',
      Cell: ({ row }) => {
        const { penalty, currency } = row.original;
        return <span className={penalty > 0 ? 'text-warning' : ''}>
          {parseFloat(penalty).toFixed(2)} {currency}
        </span>
      }
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      Cell: ({ value }) => {
        const statusObj = contractStatuses.find(s => s.value == value);
        const label = statusObj ? statusObj.label : value;
        return (<span className={value === 'ACTIVE' ? 'badge-active' : 'badge-inactive'}>
          {label}
        </span>);
      }
    },
    {
        Header: 'ACȚIUNI',
        enableCellClick: false,
        Cell: ({ row }) => (
          <div className="actions">
            <button className="edit" onClick={(e) => {e.stopPropagation();handleEditContract(row.original)}}>
              <FaEdit />
            </button>
            <button className="delete" onClick={(e) => {e.stopPropagation();deleteContract(row.original.id)}}>
              <FaTrash />
            </button>
          </div>
        )
      }      
  ];

  return (
    <div className='dms-content'>
      <Card>
        <div className='contract-section'>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h3>{debtor.name}</h3>
            {isEditing ? (
              <div className="form-actions">
                <MyButton
                  body="Renunță"
                  onClick={() => {
                    setDebtor(getDebtorById(id));
                    setIsEditing(false);
                  }}
                  style={{ maxWidth: '175px', color: 'var(--prolex-red)', borderColor: 'var(--prolex-red)' }}
                />
                <MyButton
                  body="Salvează"
                  onClick={handleSave}
                  style={{ maxWidth: '245px', color: 'var(--prolex-blue)', borderColor: 'var(--prolex-blue)' }}
                  type="submit"
                />
              </div>
            ) : (
              <div className="form-actions">
                <MyButton
                  body="Editează"
                  onClick={() => setIsEditing(true)}
                  style={{ maxWidth: '200px' }}
                />
              </div>
            )}
          </div>

          <form className='contract-details-grid'>
            <div className='form-row'>
              <h3 className="section-subtitle">Date Debitor</h3>
              <InputWrapper
                body="Nume Companie"
                value={debtor.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={!isEditing}
                isRequired
              />
              <InputWrapper
                body="Cod Fiscal (CIF)"
                value={debtor.cif}
                onChange={(e) => handleChange('cif', e.target.value)}
                disabled={!isEditing}
                isRequired
              />
              <InputWrapper
                body="Adresă"
                value={debtor.address}
                onChange={(e) => handleChange('address', e.target.value)}
                disabled={!isEditing}
              />
              <InputWrapper
                body="Telefon"
                value={debtor.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                disabled={!isEditing}
              />
              <InputWrapper
                body="Email"
                value={debtor.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={!isEditing}
              />
              <InputWrapper
                body="Persoană de Contact"
                value={debtor.contactPerson}
                onChange={(e) => handleChange('contactPerson', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div className='form-row'>
              <h3 className="section-subtitle">Statistici Financiare</h3>
              <InputWrapper
                body="Total Penalități (MDL)"
                value={debtor.totalPenalty}
                disabled={true}
                isNumeric
              />
              <InputWrapper
                body="Total Datorii (MDL)"
                value={debtor.totalDebt}
                disabled={true}
                isNumeric
              />
              <InputWrapper
                body="Număr Contracte"
                value={debtor.totalContracts}
                disabled={true}
                isNumeric
              />
              <MySelect
                options={debtorStatuses}
                value={debtorStatuses.find(option => option.value === debtor.status)}
                disabled={!isEditing}
                onChange={(selectedOption) => handleChange('status', selectedOption || '')}
              />
            </div>
          </form>
        </div>
      </Card>

      <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom:"1rem" }}><h3 style={{alignSelf:"center"}}>Lista Contractelor</h3>
        <MyButton
        style={{maxWidth:"245px",color:"var(--prolex-blue)",borderColor:"var(--prolex-blue)"}}
        id="addSettlementBtn"
        body={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><FaPlus/>Adaugă Contract Nou</span>}
        type="button"
        onClick={handleOverlay}
        />
        </div>
        <GenericTable data={contracts} columns={contractColumns}  onRowClick={(row) => navigate(`/dms/contracts/${row.original.id}`)}/>
      </Card>
      <Overlay
        handleOverlay={handleOverlay}
        isOpen={overlay}
        maxWidth="55vw"
      >
        <h2 style={{marginBottom:"2rem"}}>Adaugă Contract Nou</h2>
        <form onSubmit={submitContract} className='form-container'>
        <InputWrapper
          isRequired={true}
          value={contract.name}
          onChange={(e) => setContract(prev => ({...prev, name:e.target.value}))}
          type="text"
          name='contractName'
          body={<>Număr/Denumire Contract <span style={{color:"red"}}>*</span></>}
        />

        <div className='myInpWrapper'>
          <label htmlFor="datepicker2" className='myInpLabel'>Data Încheierii<span style={{color:"red"}}>*</span></label>
            <CustomDatePicker 
              value={contract.startDate} 
              onChange={(date) => {setContract(prev => ({ ...prev, startDate: date }));}} 
              name="datepicker2"
              isRequired={true}
            />
        </div>
        <div className='myInpWrapper'>
          <label htmlFor="datepicker2" className='myInpLabel'>Data Obligațiunii<span style={{color:"red"}}>*</span></label>
            <CustomDatePicker
              value={contract.obligationDate} 
              onChange={(date) => {setContract(prev => ({ ...prev, obligationDate: date }));}} 
              name="datepicker2"
              isRequired={true}
            />
        </div>
        <div className='myInpWrapper'>
        <label htmlFor="datepicker2" className='myInpLabel'>Finalizarea Contractului<span style={{color:"red"}}>*</span></label>
            <CustomDatePicker 
              value={contract.endDate} 
              onChange={(date) => {setContract(prev => ({ ...prev, endDate: date }));}} 
              name="datepicker2"
              isRequired={true}
            />
        </div>
        <InputWrapper
          value={contract.value}
          onChange={(e) => setContract(prev => ({...prev, value:e.target.value}))}
          type="text"
          name='contractValue'
          body={<>Valoarea Totală a Contractului <span style={{color:"red"}}>*</span></>}
          isNumeric={true}
          isRequired={true}
        />
        <div className='myInpWrapper'>
          <label htmlFor={"debtors"} className={'myInpLabel'}>Valuta</label>
            <MySelect
              options={currencies}
              name="currencies"
              value={currencies.find(option => option.value === contract.currency)}
              onChange={(selectedOption) => {setContract(prev => ({ ...prev, currency: selectedOption || '' }))}}
            />
        </div>
        <InputWrapper
          value={contract.penaltyPercentPerDay}
          onChange={(e) => setContract(prev => ({...prev, penaltyPercentPerDay:e.target.value}))}
          type="text"
          name='contractValue'
          body="Procent Penalitate Zilnică (%)"
          isNumeric={true}
        />
        <div className='myInpWrapper'>
          <label htmlFor={"debtors"} className={'myInpLabel'}>Frecvența Plăților</label>
            <MySelect 
              options={paysFrequency}
              name="paysFrequency"
              value={paysFrequency.find(option => option.value === contract.payFrequency)}
              onChange={(selectedOption) => setContract(prev => ({ ...prev, payFrequency: selectedOption || '' }))}
            />
        </div>
        <InputWrapper
          value={contract.initialDue}
          onChange={(e) => setContract(prev => ({...prev, initialDue:e.target.value}))}
          type="text"
          name='contractInitialDue'
          body={<>Suma Inițială Datorată <span style={{color:"red"}}>*</span></>}
          isNumeric={true}
          isRequired={true}
        />
        <div className='myInpWrapper'>
          <label htmlFor={"debtors"} className={'myInpLabel'}>Status Contract</label>
            <MySelect 
              options={contractStatuses.filter(option => option.value !== 'Toți')}
              name="contractStatuses"
              value={contractStatuses.find(option => option.value === contract.status)}
              onChange={(selectedOption) => setContract(prev => ({ ...prev, status: selectedOption || '' }))}
            />
        </div>
        <div className='flex-row' style={{justifyContent:"flex-end"}}>
          <MyButton
            style={{maxWidth:"175px",color:"var(--prolex-red)",borderColor:"var(--prolex-red)"}}
            id="cancelBtn"
            body="Anulează"
            onClick={handleOverlay}
          />
          <MyButton
            style={{maxWidth:"245px",color:"var(--prolex-blue)",borderColor:"var(--prolex-blue)"}}
            id="addContractBtn"
            body={<>Salvează Contract</>}
            type='submit'
          />
        </div>
      </form>
      </Overlay>
    </div>
  );
}
